import React from 'react';

import { Text } from '@change/ds-core-ui';
import { CheckIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';

type TaskProgressIndicatorProps = {
	completed: number;
	total: number;
};

export const TaskProgressIndicator: React.FC<TaskProgressIndicatorProps> = ({ completed, total }) => {
	const actualPercentage = total > 0 ? (completed / total) * 100 : 0;
	// Map 0-100% to 5-100% range (always show at least 5% fill)
	const percentage = 5 + actualPercentage * 0.95;
	const radius = 29;
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (percentage / 100) * circumference;
	const allCompleted = completed === total && total > 0;

	if (allCompleted) {
		// All completed state - green circle with checkmark
		return (
			<div className="flex items-center gap-3">
				<Text variant="body" className="text-typography-success" as="p">
					<Translate value="corgi.petition.starter_dashboard_v2.next_steps.components.task_progress_indicator.all_completed" />
				</Text>
				<div className="relative size-16 shrink-0">
					<svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 64 64">
						<circle cx="32" cy="32" r="29" stroke="#22C024" strokeWidth="6" />
					</svg>
					<div className="absolute inset-0 flex items-center justify-center">
						<CheckIcon size={32} className="border-stroke-success-strong" />
					</div>
				</div>
			</div>
		);
	}

	// In progress state
	return (
		<div className="relative size-16 shrink-0">
			<svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 64 64">
				<circle cx="32" cy="32" r="29" stroke="rgba(64, 58, 73, 0.1)" strokeWidth="6" />
				<path
					d="M32 3 A29 29 0 1 1 32 61 A29 29 0 1 1 32 3"
					stroke="url(#progressGradient)"
					strokeWidth="6"
					strokeDasharray={circumference}
					strokeDashoffset={offset}
					strokeLinecap="round"
					style={{ transition: 'stroke-dashoffset 0.3s ease' }}
				/>
				<defs>
					<linearGradient
						id="progressGradient"
						x1="63.8336"
						y1="31.8336"
						x2="-0.166406"
						y2="31.8336"
						gradientUnits="userSpaceOnUse"
					>
						<stop offset="0.341346" stopColor="#0038BC" />
						<stop offset="0.754808" stopColor="#4771FF" />
						<stop offset="0.927885" stopColor="#4AAEFF" />
					</linearGradient>
				</defs>
			</svg>
			<div className="absolute inset-0 flex items-center justify-center text-center">
				<Text variant="body" size="small" fontWeight="bold" className="text-typography-strong leading-5">
					{completed}
				</Text>
				<Text variant="body" size="small" className="text-typography-strong leading-5">
					/{total}
				</Text>
			</div>
		</div>
	);
};
